// ════════════════════════════════════════════════════════════════════
// i18n catalog — LANDING page (marketing site: nav, hero, platform strip,
// Insight+ / AI analytics / Price Tool sections, why cards, POS partners,
// pricing, demo form, footer + the translated story cards in the hero art).
//
// Scope: STATIC, human-readable UI copy only. We DO NOT translate dynamic
// data, figures/currency, platform names (Deliveroo/Talabat/Noon/…), or
// brand names (Netrev/Netrev.ai/Stripe/Pro). Amounts are passed in through a
// {price} placeholder so they stay in one place (lib/pricing.js).
//
// Keys are stable + descriptive under the `landing.*` namespace and are the
// contract Landing.js references via t("landing.<key>").
// ════════════════════════════════════════════════════════════════════

export const en = {
  // ── NAV ───────────────────────────────────────────────────────────────
  "landing.nav.product": "Product",
  "landing.nav.partners": "Partners",
  "landing.nav.ourStory": "Our story",
  "landing.nav.pricing": "Pricing",
  "landing.nav.contact": "Contact",
  "landing.nav.menuAria": "Menu",
  "landing.nav.signIn": "Sign in",
  "landing.nav.startFree": "Start free",

  // ── MOBILE MENU DRAWER ──────────────────────────────────────────────────
  "landing.mobileMenu.bookDemo": "Book a demo",
  "landing.backToTop": "Back to top",

  // ── HERO ────────────────────────────────────────────────────────────────
  "landing.hero.h1": "Food delivery sales simplified",
  "landing.hero.sub":
    "AI analytics software for food delivery sales, identifies losses and recommends action from CPC reduction to change in discount and platform allocation.",
  "landing.hero.ctaTry": "Try free for 7 days",
  "landing.hero.ctaDemo": "Book a demo",
  "landing.hero.metaTrial": "7 day free trial: Daily Log & true profit P&L",
  "landing.hero.metaNoCard": "No card needed",
  "landing.hero.metaAfterTrial": "{price}/location/month after trial",
  "landing.hero.metaCancel": "Cancel anytime",
  "landing.hero.artAlt":
    "One week at our own restaurant: 30,000 AED on the POS delivery report, but only 14,500 AED landed in the bank.",

  // ── HERO ART — story cards (the rest of the art is product UI) ─────────
  "landing.art.gap.eyebrow": "One week · our own restaurant",
  "landing.art.gap.pos": "POS delivery report",
  "landing.art.gap.bank": "Landed in the bank",
  "landing.art.gap.deducted": "Platform deductions",
  "landing.art.tracked": "Deductions, worked out",
  "landing.art.ded.commission": "Commission",
  "landing.art.ded.vat": "VAT on fees",
  "landing.art.ded.card": "Card fees",
  "landing.art.ded.cpc": "CPC ads",
  "landing.art.ded.subscription": "Subscription",
  // Floating cards in the product illustrations ({p} = platform name)
  "landing.art.ai.tag": "AI insight",
  "landing.art.ai.title": "Noon is losing you money on CPC ads.",
  "landing.art.ai.action": "Cut CPC 40%",
  "landing.art.perMonth": "{price} / month",
  "landing.art.weekDeductions": "This week's deductions",
  "landing.art.bubble.in": "Your sales & fees",
  "landing.art.bubble.out": "Recommended actions",
  "landing.art.toggle.cpc": "CPC spend",
  "landing.art.toggle.discounts": "Discounts",
  "landing.art.rec.tagCpc": "CPC",
  "landing.art.rec.tagDiscount": "Discount",
  "landing.art.rec.tagPlatform": "Platform",
  "landing.art.rec.cpc": "Cut CPC on {p} by 40%",
  "landing.art.rec.discount": "End 30% off on {p}",
  "landing.art.rec.grow": "Grow {p}: best margin at 25%",
  "landing.art.rec.losing": "Losing {price} per order",
  "landing.art.price.campaign": "30% off · {p}",
  "landing.art.price.profitPerOrder": "Profit per order",
  "landing.art.price.loses": "Loses money while it runs",
  "landing.art.price.discount": "Discount",
  "landing.art.price.campaignFee": "Campaign fee",

  // ── PLATFORM STRIP ──────────────────────────────────────────────────────
  "landing.strip.label": "One P&L for every platform you sell on",

  // ── INSIGHT+ ────────────────────────────────────────────────────────────
  "landing.p1.eyebrow": "Insight+",
  "landing.p1.h2": "Instant P&L on every sale you log",
  "landing.p1.lead":
    "You were told “30% commission on net”. Nobody mentioned the VAT on that commission, the card fees, or the ads you paid to win the order.",
  "landing.p1.b1.t": "What each platform should pay you",
  "landing.p1.b1.d": "After commission, VAT on fees at your location's rate, card fees, CPC ads and subscriptions.",
  "landing.p1.b2.t": "Profit by platform",
  "landing.p1.b2.d": "Not just revenue by platform. See which apps actually pay you.",
  "landing.p1.b3.t": "Daily, not at month end",
  "landing.p1.b3.d": "Log the day's sales for each platform. Netrev does the deductions, so you see true profit before the statement arrives.",
  "landing.p1.artAlt": "Insight+ showing gross sales, net receivable and true profit for each delivery platform.",

  // ── AI ANALYTICS ────────────────────────────────────────────────────────
  "landing.p2.eyebrow": "AI analytics",
  "landing.p2.h2": "An AI agent, not a report",
  "landing.p2.lead": "It investigates your sales and tells you what to do next.",
  "landing.p2.b1.t": "Losses spotted early",
  "landing.p2.b1.d": "Losing platforms, campaigns and discounts flagged before month end.",
  "landing.p2.b2.t": "Where CPC pays off",
  "landing.p2.b2.d": "See which platform turns CPC into the most profit.",
  "landing.p2.b3.t": "Your next move",
  "landing.p2.b3.d": "Cut CPC, change a discount or move orders to the platform that pays.",
  "landing.p2.artAlt":
    "Netrev AI reading commission, VAT, card fees, CPC, discounts and subscriptions, then recommending what to change.",

  // ── PRICE TOOL ──────────────────────────────────────────────────────────
  "landing.p3.eyebrow": "Price Tool",
  "landing.p3.h2": "Test a discount before you run it",
  "landing.p3.lead": "Build discounts and campaign fees into your menu prices.",
  "landing.p3.b1.t": "Profit per menu item",
  "landing.p3.b1.d": "See what every dish really makes on every platform.",
  "landing.p3.b2.t": "Campaign check",
  "landing.p3.b2.d": "Know if a platform still pays you while a discount runs.",
  "landing.p3.b3.t": "Keep losing items out of promos",
  "landing.p3.b3.d": "Spot the dishes that lose money in a campaign.",
  "landing.p3.artAlt":
    "Price Tool showing profit per order for each menu item, and a 30% campaign that turns an order into a loss.",

  // ── WHY ─────────────────────────────────────────────────────────────────
  "landing.why.h2": "Why operators choose Netrev",
  "landing.why.c1.t": "Built by operators",
  "landing.why.c1.d": "Years of running restaurants and cloud kitchens in several countries.",
  "landing.why.c2.t": "Live in four markets",
  "landing.why.c2.d": "UK, UAE, India and USA. Launched July 2026.",
  "landing.why.c3.t": "Web and mobile app",
  "landing.why.c3.d": "Log sales or automate them by connecting UrbanPiper or Deliverect.",

  // ── POS & MIDDLEWARE PARTNERS ───────────────────────────────────────────
  "landing.partners.eyebrow": "For POS and middleware partners",
  "landing.partners.h2": "One integration. Real profit for every restaurant you serve.",
  "landing.partners.lead": "Your reports stop at gross and net sales. Netrev works out what each platform should pay after its fees.",
  "landing.partners.b1.t": "Instant delivery sales reconciliation",
  "landing.partners.b1.d": "Profit or loss: see the deductions live on every order.",
  "landing.partners.b2.t": "Stronger retention",
  "landing.partners.b2.d": "Owners stay as they get insight on every order across every delivery partner.",
  "landing.partners.b3.t": "A light build",
  "landing.partners.b3.d":
    "You send gross and net sales by order and platform. We send back true net, P&L, ad returns and AI recommendations.",
  "landing.partners.f1.t": "Module in your product",
  "landing.partners.f1.d": "Netrev inside your reporting",
  "landing.partners.f2.t": "Direct API",
  "landing.partners.f2.d": "Sales out, true net back",
  "landing.partners.f3.t": "Fully white labelled",
  "landing.partners.f3.d": "Netrev under your brand",
  "landing.partners.tiers": "Start with Insight+ reporting, then add AI analytics and the Price Tool. We agree the format with you.",
  "landing.partners.cta": "Book a 30 minute call",
  "landing.partners.artAlt":
    "Two way integration: your POS sends gross and net sales, Netrev sends back true net, P&L, ad returns and AI recommendations.",
  "landing.partners.art.pos": "Your POS or middleware",
  "landing.partners.art.posSub": "Orders, gross and net sales by platform",
  "landing.partners.art.netrev": "Calculates true net",
  "landing.partners.art.terms": "Applies each brand's own terms",
  "landing.partners.art.in": "Gross and net sales in",
  "landing.partners.art.out": "True net, P&L, ad returns and AI tips back",

  // ── PRICING ───────────────────────────────────────────────────────────────
  "landing.pricing.h2": "One price. Per location.",
  "landing.pricing.sub": "Every feature, every platform. Each location runs on its own.",
  "landing.pricing.billingAria": "Billing period",
  "landing.pricing.currencyAria": "Currency",
  "landing.pricing.toggle.monthly": "Monthly",
  "landing.pricing.toggle.annual": "Annual",
  "landing.pricing.toggle.oneMonthFree": "1 month free",
  "landing.pricing.perWorkspace": "Per workspace",
  "landing.pricing.perYear": "/year",
  "landing.pricing.perMonth": "/month",
  "landing.pricing.save": "Save {price}: one month free",
  "landing.pricing.additional": "Each extra workspace: {price}{period}",
  "landing.pricing.feat.breakdown": "Daily P&L with Excel export",
  "landing.pricing.feat.aiInsights": "AI powered insights & analysis",
  "landing.pricing.feat.priceTool": "Price Tool for menus and campaigns",
  "landing.pricing.feat.apps": "Mobile app + web portal",
  "landing.pricing.feat.cancel": "Cancel any workspace independently",
  "landing.pricing.cta": "Start 7 day free trial",
  "landing.pricing.note": "7 day free trial: Daily Log & true profit P&L. AI insights and the Price Tool are on the paid plan. No card needed.",

  // ── CONTACT — demo request form + Calendly booking ─────────────────────
  "landing.contact.h2": "See it live in 30 minutes",
  "landing.contact.sub": "Book a call and live demo, or send us a message. We reply within a day.",
  "landing.contact.preferEmail": "Prefer email?",
  "landing.contact.form.eyebrow": "Quick form",
  "landing.contact.form.title": "Request a demo",
  "landing.contact.form.titleBook": "Book a demo call",
  "landing.contact.form.nameLabel": "Your Name ",
  "landing.contact.form.namePlaceholder": "John Smith",
  "landing.contact.form.emailLabel": "Work Email ",
  "landing.contact.form.emailPlaceholder": "john@restaurant.com",
  "landing.contact.form.companyLabel": "Restaurant Name ",
  "landing.contact.form.companyOptional": "(optional)",
  "landing.contact.form.companyPlaceholder": "Your Restaurant",
  "landing.contact.form.messageLabel": "What would you like to see in the demo? ",
  "landing.contact.form.messagePlaceholder":
    "e.g. We run 3 restaurants on Deliveroo + Uber Eats and want to see how the AI insights would work with our data",
  "landing.contact.form.errValidation": "Please fill in name, email and message.",
  "landing.contact.form.errSend": "Couldn't send. Please email hello@netrev.ai directly.",
  "landing.contact.form.sent": "Thanks, we'll be in touch within 24 hours.",
  "landing.contact.form.sending": "Sending…",
  "landing.contact.form.submit": "Request Demo →",
  "landing.contact.form.disclaimer":
    "By submitting, you agree to be contacted by Netrev. We don't share your details with anyone.",
  "landing.contact.tab.demo": "Request a demo",
  "landing.contact.tab.book": "Book a call",
  "landing.contact.book.noticeBold": "Pick a time that works for you.",
  "landing.contact.book.noticeRest": "30 minute call on Google Meet. The invite goes straight to your inbox.",
  "landing.contact.book.iframeTitle": "Book a Netrev demo",
  "landing.contact.book.noSlot": "Can't find a slot that works?",
  "landing.contact.book.openFull": "Open full calendar",

  // ── APP STORE BADGES + QR (footer) ────────────────────────────────────────
  "landing.app.badge.iosTop": "Download on the",
  "landing.app.badge.androidTop": "Get it on",
  "landing.app.badge.iosAria": "Download Netrev on the App Store",
  "landing.app.badge.androidAria": "Get Netrev on Google Play",
  "landing.app.qr.ios": "Scan to download (iOS)",
  "landing.app.qr.android": "Scan to download (Android)",

  // ── FOOTER ────────────────────────────────────────────────────────────────
  "landing.footer.tagline": "Real profit on every sale you log.",
  "landing.footer.col.product": "Product",
  "landing.footer.col.company": "Company",
  "landing.footer.col.legal": "Legal",
  "landing.footer.col.app": "Get the app",
  "landing.footer.link.pricing": "Pricing",
  "landing.footer.link.signIn": "Sign in",
  "landing.footer.link.contact": "Contact",
  "landing.footer.link.bookDemo": "Book a demo",
  "landing.footer.legal.privacy": "Privacy",
  "landing.footer.legal.terms": "Terms",
  "landing.footer.legal.cookies": "Cookies",
  "landing.footer.rights": "All rights reserved",
  "landing.footer.madeIn": "Made in the UK · Built for restaurants in the UK, UAE, India and USA",
};

export const ar = {
  // ── NAV ───────────────────────────────────────────────────────────────
  "landing.nav.product": "المنتج",
  "landing.nav.partners": "الشركاء",
  "landing.nav.ourStory": "قصتنا",
  "landing.nav.pricing": "الأسعار",
  "landing.nav.contact": "تواصل معنا",
  "landing.nav.menuAria": "القائمة",
  "landing.nav.signIn": "تسجيل الدخول",
  "landing.nav.startFree": "ابدأ مجانًا",

  // ── MOBILE MENU DRAWER ──────────────────────────────────────────────────
  "landing.mobileMenu.bookDemo": "احجز عرضًا توضيحيًا",
  "landing.backToTop": "العودة إلى الأعلى",

  // ── HERO ────────────────────────────────────────────────────────────────
  "landing.hero.h1": "مبيعات توصيل الطعام، ببساطة",
  "landing.hero.sub":
    "برنامج تحليلات بالذكاء الاصطناعي لمبيعات توصيل الطعام، يكشف الخسائر ويوصي بالإجراء المناسب، من خفض إنفاق CPC إلى تعديل الخصومات وتوزيع الطلبات على المنصات.",
  "landing.hero.ctaTry": "جرّب مجانًا لمدة 7 أيام",
  "landing.hero.ctaDemo": "احجز عرضًا توضيحيًا",
  "landing.hero.metaTrial": "تجربة مجانية لمدة 7 أيام: السجل اليومي وحساب الربح الحقيقي",
  "landing.hero.metaNoCard": "بدون بطاقة",
  "landing.hero.metaAfterTrial": "{price}/موقع/شهر بعد التجربة",
  "landing.hero.metaCancel": "ألغِ في أي وقت",
  "landing.hero.artAlt":
    "أسبوع واحد في مطعمنا: 30,000 درهم في تقرير التوصيل بنقطة البيع، لكن ما وصل إلى البنك 14,500 درهم فقط.",

  // ── HERO ART — story cards ──────────────────────────────────────────────
  "landing.art.gap.eyebrow": "أسبوع واحد · في مطعمنا",
  "landing.art.gap.pos": "تقرير التوصيل في نقطة البيع",
  "landing.art.gap.bank": "ما وصل إلى البنك",
  "landing.art.gap.deducted": "ما استقطعته المنصات",
  "landing.art.tracked": "استقطاعات نحسبها لك",
  "landing.art.ded.commission": "العمولة",
  "landing.art.ded.vat": "ضريبة الرسوم",
  "landing.art.ded.card": "رسوم البطاقات",
  "landing.art.ded.cpc": "إعلانات CPC",
  "landing.art.ded.subscription": "الاشتراك",
  // Floating cards in the product illustrations ({p} = platform name)
  "landing.art.ai.tag": "توصية الذكاء الاصطناعي",
  "landing.art.ai.title": "إعلانات CPC على Noon تكبّدك خسارة.",
  "landing.art.ai.action": "خفّض CPC بنسبة 40%",
  "landing.art.perMonth": "{price} شهريًا",
  "landing.art.weekDeductions": "استقطاعات هذا الأسبوع",
  "landing.art.bubble.in": "مبيعاتك ورسومك",
  "landing.art.bubble.out": "الإجراءات المقترحة",
  "landing.art.toggle.cpc": "إنفاق CPC",
  "landing.art.toggle.discounts": "الخصومات",
  "landing.art.rec.tagCpc": "CPC",
  "landing.art.rec.tagDiscount": "الخصم",
  "landing.art.rec.tagPlatform": "المنصة",
  "landing.art.rec.cpc": "خفّض CPC على {p} بنسبة 40%",
  "landing.art.rec.discount": "أوقف خصم 30% على {p}",
  "landing.art.rec.grow": "وسّع مبيعاتك على {p}: أفضل هامش 25%",
  "landing.art.rec.losing": "خسارة {price} لكل طلب",
  "landing.art.price.campaign": "خصم 30% · {p}",
  "landing.art.price.profitPerOrder": "الربح لكل طلب",
  "landing.art.price.loses": "خاسر طوال مدة الحملة",
  "landing.art.price.discount": "الخصم",
  "landing.art.price.campaignFee": "رسوم الحملة",

  // ── PLATFORM STRIP ──────────────────────────────────────────────────────
  "landing.strip.label": "أرباح وخسائر واحدة لكل منصة تبيع عليها",

  // ── INSIGHT+ ────────────────────────────────────────────────────────────
  "landing.p1.eyebrow": "رؤى+",
  "landing.p1.h2": "أرباح وخسائر فورية لكل عملية بيع تسجّلها",
  "landing.p1.lead":
    "قيل لك «عمولة 30% على الصافي». لكن لم يذكر أحد ضريبة القيمة المضافة على تلك العمولة، ولا رسوم البطاقات، ولا الإعلانات التي دفعت ثمنها لتحصل على الطلب.",
  "landing.p1.b1.t": "ما يُفترض أن تدفعه لك كل منصة",
  "landing.p1.b1.d": "بعد استقطاع العمولة، وضريبة القيمة المضافة على الرسوم بالنسبة المعتمدة لموقعك، ورسوم البطاقات، وإعلانات CPC، والاشتراكات.",
  "landing.p1.b2.t": "الربح لكل منصة",
  "landing.p1.b2.d": "لا تكتفِ بالإيرادات: اعرف أي التطبيقات تحقق لك ربحًا فعلًا.",
  "landing.p1.b3.t": "يوميًا، لا في نهاية الشهر",
  "landing.p1.b3.d": "سجّل مبيعات اليوم لكل منصة، ويحتسب Netrev الاستقطاعات لترى ربحك الحقيقي قبل وصول كشف الحساب.",
  "landing.p1.artAlt": "رؤى+ تعرض إجمالي المبيعات وصافي المستحقات والربح الحقيقي لكل منصة توصيل.",

  // ── AI ANALYTICS ────────────────────────────────────────────────────────
  "landing.p2.eyebrow": "تحليلات الذكاء الاصطناعي",
  "landing.p2.h2": "وكيل ذكاء اصطناعي، لا مجرد تقرير",
  "landing.p2.lead": "يحلّل مبيعاتك ويخبرك بخطوتك التالية.",
  "landing.p2.b1.t": "اكتشاف الخسائر مبكرًا",
  "landing.p2.b1.d": "تنبيه بالمنصات والحملات والخصومات الخاسرة قبل نهاية الشهر.",
  "landing.p2.b2.t": "أين يُثمر إنفاق CPC",
  "landing.p2.b2.d": "اعرف أي منصة تحوّل إنفاق CPC إلى أعلى ربح.",
  "landing.p2.b3.t": "خطوتك التالية",
  "landing.p2.b3.d": "خفّض إنفاق CPC، أو عدّل الخصم، أو انقل الطلبات إلى المنصة الأكثر ربحًا.",
  "landing.p2.artAlt":
    "ذكاء Netrev الاصطناعي يقرأ العمولة وضريبة القيمة المضافة ورسوم البطاقات وإنفاق CPC والخصومات والاشتراكات، ثم يوصي بما يجب تغييره.",

  // ── PRICE TOOL ──────────────────────────────────────────────────────────
  "landing.p3.eyebrow": "أداة التسعير",
  "landing.p3.h2": "اختبر الخصم قبل أن تطلقه",
  "landing.p3.lead": "احتسب الخصومات ورسوم الحملات ضمن أسعار قائمتك.",
  "landing.p3.b1.t": "الربح لكل صنف",
  "landing.p3.b1.d": "اعرف ما يحققه كل طبق فعلًا على كل منصة.",
  "landing.p3.b2.t": "فحص الحملات",
  "landing.p3.b2.d": "اعرف إن كانت المنصة لا تزال تحقق لك ربحًا أثناء الخصم.",
  "landing.p3.b3.t": "أبعِد الأصناف الخاسرة عن العروض",
  "landing.p3.b3.d": "اكتشف الأطباق التي تخسر المال في أي حملة.",
  "landing.p3.artAlt":
    "أداة التسعير تعرض الربح لكل طلب لكل صنف، وحملة خصم 30% تحوّل الطلب إلى خسارة.",

  // ── WHY ─────────────────────────────────────────────────────────────────
  "landing.why.h2": "لماذا يختار المشغّلون Netrev",
  "landing.why.c1.t": "بناه مشغّلو مطاعم",
  "landing.why.c1.d": "سنوات من إدارة المطاعم والمطابخ السحابية في عدة دول.",
  "landing.why.c2.t": "متاح في أربعة أسواق",
  "landing.why.c2.d": "المملكة المتحدة والإمارات والهند والولايات المتحدة. انطلق في يوليو 2026.",
  "landing.why.c3.t": "الويب وتطبيق الجوال",
  "landing.why.c3.d": "سجّل مبيعاتك أو أتمِتها بربط UrbanPiper أو Deliverect.",

  // ── POS & MIDDLEWARE PARTNERS ───────────────────────────────────────────
  "landing.partners.eyebrow": "لشركاء أنظمة نقاط البيع والأنظمة الوسيطة",
  "landing.partners.h2": "تكامل واحد. ربح حقيقي لكل مطعم تخدمه.",
  "landing.partners.lead": "تتوقف تقاريرك عند إجمالي المبيعات وصافيها. ويحسب Netrev ما يُفترض أن تحوّله كل منصة بعد استقطاع رسومها.",
  "landing.partners.b1.t": "مطابقة فورية لمبيعات التوصيل",
  "landing.partners.b1.d": "ربح أم خسارة؟ شاهد الاستقطاعات مباشرةً على كل طلب.",
  "landing.partners.b2.t": "ولاء أقوى للعملاء",
  "landing.partners.b2.d": "يبقى أصحاب المطاعم معك لأنهم يحصلون على رؤية واضحة لكل طلب عبر كل منصة توصيل.",
  "landing.partners.b3.t": "تكامل خفيف",
  "landing.partners.b3.d":
    "ترسل إجمالي المبيعات وصافيها لكل طلب ومنصة، ونعيد إليك الصافي الحقيقي والأرباح والخسائر وعائد الإعلانات وتوصيات الذكاء الاصطناعي.",
  "landing.partners.f1.t": "وحدة داخل منتجك",
  "landing.partners.f1.d": "Netrev ضمن تقاريرك",
  "landing.partners.f2.t": "واجهة API مباشرة",
  "landing.partners.f2.d": "ترسل المبيعات، وتستلم الصافي الحقيقي",
  "landing.partners.f3.t": "بعلامتك التجارية بالكامل",
  "landing.partners.f3.d": "Netrev باسم علامتك",
  "landing.partners.tiers": "ابدأ بتقارير رؤى+، ثم أضف تحليلات الذكاء الاصطناعي وأداة التسعير. ونتفق معك على صيغة التكامل.",
  "landing.partners.cta": "احجز مكالمة لمدة 30 دقيقة",
  "landing.partners.artAlt":
    "تكامل باتجاهين: يرسل نظام نقاط البيع إجمالي المبيعات وصافيها، ويعيد Netrev الصافي الحقيقي والأرباح والخسائر وعائد الإعلانات وتوصيات الذكاء الاصطناعي.",
  "landing.partners.art.pos": "نظام نقاط البيع أو النظام الوسيط",
  "landing.partners.art.posSub": "الطلبات وإجمالي المبيعات وصافيها لكل منصة",
  "landing.partners.art.netrev": "يحسب الصافي الحقيقي",
  "landing.partners.art.terms": "وفق شروط كل علامة تجارية",
  "landing.partners.art.in": "إجمالي المبيعات وصافيها",
  "landing.partners.art.out": "الصافي الحقيقي والأرباح وعائد الإعلانات والتوصيات",

  // ── PRICING ───────────────────────────────────────────────────────────────
  "landing.pricing.h2": "سعر واحد. لكل موقع.",
  "landing.pricing.sub": "كل الميزات، وكل المنصات. يعمل كل موقع بشكل مستقل.",
  "landing.pricing.billingAria": "فترة الفوترة",
  "landing.pricing.currencyAria": "العملة",
  "landing.pricing.toggle.monthly": "شهري",
  "landing.pricing.toggle.annual": "سنوي",
  "landing.pricing.toggle.oneMonthFree": "شهر مجاني",
  "landing.pricing.perWorkspace": "لكل مساحة عمل",
  "landing.pricing.perYear": "/سنة",
  "landing.pricing.perMonth": "/شهر",
  "landing.pricing.save": "وفّر {price}: شهر مجاني",
  "landing.pricing.additional": "كل مساحة عمل إضافية: {price}{period}",
  "landing.pricing.feat.breakdown": "أرباح وخسائر يومية مع تصدير Excel",
  "landing.pricing.feat.aiInsights": "رؤى وتحليلات مدعومة بالذكاء الاصطناعي",
  "landing.pricing.feat.priceTool": "أداة تسعير للقوائم والحملات",
  "landing.pricing.feat.apps": "تطبيق جوال + بوابة ويب",
  "landing.pricing.feat.cancel": "ألغِ أي مساحة عمل بشكل مستقل",
  "landing.pricing.cta": "ابدأ تجربة مجانية لمدة 7 أيام",
  "landing.pricing.note": "تجربة مجانية لمدة 7 أيام: السجل اليومي وحساب الربح الحقيقي. رؤى الذكاء الاصطناعي وأداة التسعير ضمن الخطة المدفوعة فقط. لا حاجة لبطاقة.",

  // ── CONTACT — demo request form + Calendly booking ─────────────────────
  "landing.contact.h2": "شاهده مباشرةً في 30 دقيقة",
  "landing.contact.sub": "احجز مكالمة مع عرض مباشر، أو أرسل لنا رسالة. نرد خلال يوم.",
  "landing.contact.preferEmail": "تفضّل البريد الإلكتروني؟",
  "landing.contact.form.eyebrow": "نموذج سريع",
  "landing.contact.form.title": "اطلب عرضًا توضيحيًا",
  "landing.contact.form.titleBook": "احجز مكالمة عرض توضيحي",
  "landing.contact.form.nameLabel": "اسمك ",
  "landing.contact.form.namePlaceholder": "John Smith",
  "landing.contact.form.emailLabel": "بريد العمل ",
  "landing.contact.form.emailPlaceholder": "john@restaurant.com",
  "landing.contact.form.companyLabel": "اسم المطعم ",
  "landing.contact.form.companyOptional": "(اختياري)",
  "landing.contact.form.companyPlaceholder": "مطعمك",
  "landing.contact.form.messageLabel": "ما الذي تودّ رؤيته في العرض التوضيحي؟ ",
  "landing.contact.form.messagePlaceholder":
    "مثال: نُدير 3 مطاعم على Deliveroo + Uber Eats ونريد رؤية كيف ستعمل رؤى الذكاء الاصطناعي مع بياناتنا",
  "landing.contact.form.errValidation": "يرجى إدخال الاسم والبريد الإلكتروني والرسالة.",
  "landing.contact.form.errSend": "تعذّر الإرسال. يرجى مراسلة hello@netrev.ai مباشرةً.",
  "landing.contact.form.sent": "شكرًا، سنتواصل معك خلال 24 ساعة.",
  "landing.contact.form.sending": "جارٍ الإرسال…",
  "landing.contact.form.submit": "اطلب عرضًا توضيحيًا ←",
  "landing.contact.form.disclaimer":
    "بإرسالك النموذج، فأنت توافق على أن يتواصل معك Netrev. نحن لا نشارك بياناتك مع أي أحد.",
  "landing.contact.tab.demo": "اطلب عرضًا توضيحيًا",
  "landing.contact.tab.book": "احجز مكالمة",
  "landing.contact.book.noticeBold": "اختر الوقت الذي يناسبك.",
  "landing.contact.book.noticeRest": "مكالمة لمدة 30 دقيقة عبر Google Meet. تصل الدعوة مباشرةً إلى بريدك الوارد.",
  "landing.contact.book.iframeTitle": "احجز عرضًا توضيحيًا لـ Netrev",
  "landing.contact.book.noSlot": "لم تجد موعدًا مناسبًا؟",
  "landing.contact.book.openFull": "افتح التقويم الكامل",

  // ── APP STORE BADGES + QR (footer) ────────────────────────────────────────
  "landing.app.badge.iosTop": "حمّله من",
  "landing.app.badge.androidTop": "احصل عليه من",
  "landing.app.badge.iosAria": "حمّل Netrev من App Store",
  "landing.app.badge.androidAria": "احصل على Netrev من Google Play",
  "landing.app.qr.ios": "امسح للتنزيل (iOS)",
  "landing.app.qr.android": "امسح للتنزيل (Android)",

  // ── FOOTER ────────────────────────────────────────────────────────────────
  "landing.footer.tagline": "ربح حقيقي على كل عملية بيع تسجّلها.",
  "landing.footer.col.product": "المنتج",
  "landing.footer.col.company": "الشركة",
  "landing.footer.col.legal": "قانوني",
  "landing.footer.col.app": "حمّل التطبيق",
  "landing.footer.link.pricing": "الأسعار",
  "landing.footer.link.signIn": "تسجيل الدخول",
  "landing.footer.link.contact": "تواصل معنا",
  "landing.footer.link.bookDemo": "احجز عرضًا توضيحيًا",
  "landing.footer.legal.privacy": "الخصوصية",
  "landing.footer.legal.terms": "الشروط",
  "landing.footer.legal.cookies": "ملفات تعريف الارتباط",
  "landing.footer.rights": "جميع الحقوق محفوظة",
  "landing.footer.madeIn": "صُنع في المملكة المتحدة · مبني للمطاعم في المملكة المتحدة والإمارات والهند والولايات المتحدة",
};

export default { en, ar };
